import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import type { NetWorthSummary, NetWorthChange } from '@/api/types';

interface NetWorthSummaryCardsProps {
    /** Summary data for Net Worth */
    netWorthSummary: NetWorthSummary;
    /** Summary data for Assets */
    assetsSummary: NetWorthSummary;
    /** Summary data for Debts */
    debtsSummary: NetWorthSummary;
    /** Display currency */
    currency: string;
    /** Whether data is loading (shows shimmer on values) */
    isLoading?: boolean;
}

/**
 * Format percentage change for display.
 */
function formatPercentage(change: NetWorthChange | null): string {
    if (!change || change.percentage === null) {
        return 'N/A';
    }
    const sign = change.percentage >= 0 ? '+' : '';
    return `${sign}${change.percentage.toFixed(1)}%`;
}

/**
 * Get color class for change values.
 * For Debts: invert logic (decrease = green/good, increase = red/bad)
 */
function getChangeColorClass(change: NetWorthChange | null, isDebt: boolean): string {
    if (!change || change.percentage === null) {
        return 'text-gray-500 dark:text-gray-400';
    }

    const isPositive = change.amount >= 0;

    if (isDebt) {
        // For debts: increase = bad (red), decrease = good (green)
        return isPositive
            ? 'text-red-600 dark:text-red-400'
            : 'text-green-600 dark:text-green-400';
    }

    // For assets and net worth: increase = good (green), decrease = bad (red)
    return isPositive
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';
}

interface SummaryCardProps {
    title: string;
    summary: NetWorthSummary;
    currency: string;
    isDebt?: boolean;
    linkTo?: string;
    isLoading?: boolean;
}

/**
 * Change indicator component - compact version for mobile, full version for desktop.
 */
function ChangeIndicator({
    label,
    change,
    currency,
    isDebt,
    compact = false,
    isLoading = false,
}: {
    label: string;
    change: NetWorthChange | null;
    currency: string;
    isDebt: boolean;
    compact?: boolean;
    isLoading?: boolean;
}) {
    const colorClass = getChangeColorClass(change, isDebt);

    // Show shimmer placeholder when loading
    if (isLoading) {
        if (compact) {
            return (
                <div className="text-xs flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">{label}</span>
                    <span
                        className="ml-1 inline-block rounded-sm bg-linear-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-shimmer bg-size-[200%_100%]"
                        style={{ width: '3rem', height: '0.875em' }}
                    />
                </div>
            );
        }
        return (
            <div className="text-sm flex items-baseline justify-between">
                <span className="text-gray-500 dark:text-gray-400 mr-3">{label}</span>
                <span className="flex items-center gap-1">
                    <span
                        className="inline-block rounded-sm bg-linear-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-shimmer bg-size-[200%_100%]"
                        style={{ width: '4rem', height: '0.875em' }}
                    />
                    <span
                        className="inline-block rounded-sm bg-linear-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-shimmer bg-size-[200%_100%]"
                        style={{ width: '3rem', height: '0.875em' }}
                    />
                </span>
            </div>
        );
    }

    if (!change) {
        if (compact) {
            return (
                <div className="text-xs">
                    <span className="text-gray-500 dark:text-gray-400">{label}</span>
                    <span className={`ml-1 ${colorClass}`}>N/A</span>
                </div>
            );
        }
        // Full layout: maintain same flex structure as when data exists
        return (
            <div className="text-sm flex items-baseline justify-between">
                <span className="text-gray-500 dark:text-gray-400 mr-3">{label}</span>
                <span className={`font-medium ${colorClass} text-right`}>N/A</span>
            </div>
        );
    }

    if (compact) {
        // Compact: "MTD +$500"
        return (
            <div className="text-xs">
                <span className="text-gray-500 dark:text-gray-400">{label}</span>
                <span className={`ml-1 font-medium ${colorClass}`}>
                    <MoneyDisplay
                        value={change.amount}
                        currency={currency}
                        size="sm"
                        showSign
                        className="inline"
                        isLoading={isLoading}
                    />
                </span>
            </div>
        );
    }

    // Full: "MTD    +$500 (+5.2%)" - label left, values right aligned
    return (
        <div className="text-sm flex items-baseline justify-between">
            <span className="text-gray-500 dark:text-gray-400 mr-3">{label}</span>
            <span className={`font-medium ${colorClass} text-right`}>
                <MoneyDisplay
                    value={change.amount}
                    currency={currency}
                    size="sm"
                    showSign
                    className="inline"
                    isLoading={isLoading}
                />
                <span className="ml-1">
                    ({formatPercentage(change)})
                </span>
            </span>
        </div>
    );
}

/**
 * Single summary card component showing current value and MTD/YTD changes.
 * Responsive layout:
 * - Desktop: Vertical layout with title, value, MTD/YTD grid
 * - Mobile: Horizontal compact layout with title, value, and changes inline
 * Assets and Debts cards are clickable and link to their respective pages.
 */
function SummaryCard({ title, summary, currency, isDebt = false, linkTo, isLoading = false }: SummaryCardProps) {
    const cardContent = (
        <>
            {/* Desktop Layout - Title and value inline, MTD/YTD stacked */}
            <div className="hidden md:block">
                {/* Title and Value inline */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {title}
                        </h3>
                        {linkTo && (
                            <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
                        )}
                    </div>
                    <MoneyDisplay
                        value={summary.currentValue}
                        currency={currency}
                        size="lg"
                        className="text-2xl font-bold text-gray-900 dark:text-white"
                        isLoading={isLoading}
                    />
                </div>

                {/* YTD Change */}
                <ChangeIndicator
                    label="YTD"
                    change={summary.ytdChange}
                    currency={currency}
                    isDebt={isDebt}
                    isLoading={isLoading}
                />
            </div>

            {/* Mobile Layout - Compact horizontal */}
            <div className="md:hidden">
                <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                {title}
                            </h3>
                            {linkTo && (
                                <ArrowRightIcon className="h-3 w-3 text-gray-400 group-hover:text-accent" />
                            )}
                        </div>
                        <MoneyDisplay
                            value={summary.currentValue}
                            currency={currency}
                            size="lg"
                            className="text-lg font-bold text-gray-900 dark:text-white"
                            isLoading={isLoading}
                        />
                    </div>
                    <div className="flex flex-col items-end text-right shrink-0 ml-2">
                        <ChangeIndicator
                            label="YTD"
                            change={summary.ytdChange}
                            currency={currency}
                            isDebt={isDebt}
                            compact
                            isLoading={isLoading}
                        />
                    </div>
                </div>
            </div>
        </>
    );

    const baseClasses = "rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary p-3 md:p-4 flex-1 transition-all";
    const hoverClasses = linkTo ? "group cursor-pointer hover:border-accent hover:shadow-md" : "";

    if (linkTo) {
        return (
            <Link to={linkTo} className={`${baseClasses} ${hoverClasses}`}>
                {cardContent}
            </Link>
        );
    }

    return (
        <div className={baseClasses}>
            {cardContent}
        </div>
    );
}

/**
 * Net Worth summary cards component displaying Net Worth, Total Assets, and Total Debts.
 * Each card shows current value with MTD and YTD change indicators.
 * Assets and Debts cards are clickable and navigate to their respective pages.
 * Responsive: compact horizontal layout on mobile, full card layout on desktop.
 */
export function NetWorthSummaryCards({
    netWorthSummary,
    assetsSummary,
    debtsSummary,
    currency,
    isLoading = false,
}: NetWorthSummaryCardsProps) {
    return (
        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <SummaryCard
                title="Net Worth"
                summary={netWorthSummary}
                currency={currency}
                isLoading={isLoading}
            />
            <SummaryCard
                title="Assets"
                summary={assetsSummary}
                currency={currency}
                linkTo="/assets"
                isLoading={isLoading}
            />
            <SummaryCard
                title="Debts"
                summary={debtsSummary}
                currency={currency}
                isDebt
                isLoading={isLoading}
                linkTo="/debts"
            />
        </div>
    );
}
